export default `#version 300 es
precision highp float;
#define GLSLIFY 1

struct Light {
    vec4 position;
    vec3 color;
    float intensity;
    bool visible;
};

#pragma defines

in vec3 worldPosition;
in vec2 vUv;
in vec3 vTangent;
in vec3 vNormal;
in float vFragDepth;
in mat3 tbn;

uniform mat4 viewMatrix;
uniform sampler2D tGrunge;
uniform sampler2D tNormal;
uniform vec3 uColor;
uniform float uEmissive;
uniform float uMask;
uniform vec3 cameraPosition;
uniform vec3 ambient;
uniform Light lights[16];
uniform samplerCube tEnvMap;
uniform float uCameraScale;

out vec4 fragData[2];
#define EPSILON 0.001f

float beckmannDistribution(float x, float roughness) {
  float NdotH = max(x, 0.0001);
  float cos2Alpha = NdotH * NdotH;
  float tan2Alpha = (cos2Alpha - 1.0) / cos2Alpha;
  float roughness2 = roughness * roughness;
  float denom = 3.141592653589793 * roughness2 * cos2Alpha * cos2Alpha;
  return exp(tan2Alpha / roughness2) / denom;
}

float cookTorranceSpecular(
  vec3 lightDirection,
  vec3 viewDirection,
  vec3 surfaceNormal,
  float roughness,
  float fresnel) {

  float VdotN = max(dot(viewDirection, surfaceNormal), 0.0);
  float LdotN = max(dot(lightDirection, surfaceNormal), 0.0);

  //Half angle vector
  vec3 H = normalize(lightDirection + viewDirection);

  //Geometric term
  float NdotH = max(dot(surfaceNormal, H), 0.0);
  float VdotH = max(dot(viewDirection, H), 0.000001);
  float LdotH = max(dot(lightDirection, H), 0.000001);
  float G1 = (2.0 * NdotH * VdotN) / VdotH;
  float G2 = (2.0 * NdotH * LdotN) / LdotH;
  float G = min(1.0, min(G1, G2));
  
  //Distribution term
  float D = beckmannDistribution(NdotH, roughness);

  //Fresnel term
  float F = pow(1.0 - VdotN, fresnel);

  //Multiply terms and done
  return  G * F * D / max(3.14159265 * VdotN, 0.000001);
}

float luma_0(vec3 color) {
  return dot(color, vec3(0.299, 0.587, 0.114));
}

float luma_0(vec4 color) {
  return dot(color.rgb, vec3(0.299, 0.587, 0.114));
}

const float maxLodLevel = 8.0;
const float far = 10000.0;

vec4 pbr(vec3 albedo, vec3 norm, float metallic, float roughness, vec3 emissive, samplerCube tEnvMap, vec3 ambient) {
    float dist = length(worldPosition - cameraPosition);
    vec3 eyeDirection = normalize(cameraPosition - worldPosition);
    vec3 diffuse = vec3(0.0);
    vec3 specular = vec3(0.0);

    vec3 F0 = vec3(0.04, 0.04, 0.04);
    F0 = mix(F0, albedo, metallic);

    for(int i = 0; i < lights.length(); i++) {
        if(!lights[i].visible) {
            continue;
        }

        vec3 lightDir = vec3(0.0);
        float intensity = lights[i].intensity;
        if(lights[i].position.w == 0.0) {
            lightDir = normalize(-lights[i].position.xyz);
        } else {
            vec3 d = lights[i].position.xyz - worldPosition;
            lightDir = normalize(d);
            float dist = length(d);
            intensity /= 1.0 + dist + pow(dist, 2.0);
            if(intensity < 0.01) {
                continue;
            }
        }
        float diff = max(dot(norm, lightDir), 0.0);
        vec3 color = lights[i].color * intensity;
        diffuse += diff * color;
        specular += cookTorranceSpecular(lightDir, eyeDirection, norm, roughness, luma_0(F0)) * color;
    }

    vec3 reflectedDir = reflect(-eyeDirection, norm);
    float lod = roughness * maxLodLevel;
    vec3 reflectionColor = textureLod(tEnvMap, reflectedDir, lod).rgb;
    float NdotV = max(dot(norm, eyeDirection), 0.0);
    vec3 F = F0 + (1.0 - F0) * pow(1.0 - NdotV, 5.0);
    vec3 reflection = reflectionColor * F;
    vec3 lighting = diffuse * (1. - metallic) + ambient + specular;

    return vec4(lighting * albedo + emissive + reflection, 1.0);
}

float luma_1(vec3 color) {
  return dot(color, vec3(0.299, 0.587, 0.114));
}

float luma_1(vec4 color) {
  return dot(color.rgb, vec3(0.299, 0.587, 0.114));
}

void main() {
    vec3 grungeMap = pow(texture(tGrunge, fract(vUv * 4.f)).rgb, vec3(1.f / 2.2f));
    float color = grungeMap.g;
    float grunge = grungeMap.r;
    float resMask = step(1.f - uMask, 1.f - grunge);
    vec3 emissive = mix(vec3(0.0f), uColor, resMask) * uEmissive;

    vec3 albedo = max(vec3(EPSILON), mix(vec3(color), uColor, resMask));
    vec3 norm = normalize(tbn * (texture(tNormal, fract(vUv * 4.f)).rgb * 2.f - 1.f));

    float metallic = 0.0f;
    float roughness = grungeMap.b;

    fragData[0] = pbr(albedo, norm, metallic, roughness, emissive, tEnvMap, ambient);
    fragData[1] = vec4(fragData[0].rgb * resMask, uEmissive);
    gl_FragDepth = log2(vFragDepth) / uCameraScale;
}
`